import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dices, ArrowLeft, ArrowRight, Instagram, ExternalLink, MessageSquarePlus, Mail, X } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
export default function HyenaDungeon() {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [media, setMedia] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      const c = await api.get("/hyena-dungeon/content");
      setContent(c.data);
      const m = await api.get("/hyena-dungeon/media");
      setMedia(m.data);
      // Update SEO
      if (c.data.seo_title) document.title = c.data.seo_title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc && c.data.seo_description) desc.setAttribute("content", c.data.seo_description);
    })();
  }, []);

  if (!content) {
    return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center font-mono text-xs tracking-widest">LOADING...</div>;
  }

  const hero = media.find((m) => m.is_hero);
  const qr = media.find((m) => m.is_qr);
  const gallery = media.filter((m) => !m.is_hero && !m.is_qr);
  const fileUrl = (id, variant = "medium") => `${API_BASE}/hyena-dungeon/media/${id}/file?variant=${variant}`;

  return (
    <div data-testid="hyena-page" className="min-h-screen bg-[#0a0505] text-white antialiased" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* SUB HEADER */}
      <div className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-red-950/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <Link to="/" data-testid="hyena-back-home" className="flex items-center gap-2 text-white hover:text-red-400 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-xs uppercase tracking-[0.3em]">TED · Home</span>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin/hyena-dungeon" data-testid="hyena-admin-link" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest border border-red-900 text-red-300 px-3 py-2 hover:border-red-500">
                Edit Page
              </Link>
            )}
            {content.social_url && (
              <a href={content.social_url} target="_blank" rel="noreferrer noopener" data-testid="hyena-social-top" className="inline-flex items-center gap-1.5 bg-red-600 text-black text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-red-500">
                <ExternalLink className="w-3.5 h-3.5" /> Linktree
              </a>
            )}
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden pt-14">
        {/* Background */}
        <div className="absolute inset-0">
          {hero ? (
            <img src={fileUrl(hero.id, "full")} alt={hero.alt_text || "Hyena Dungeon hero"} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1a0505] via-[#0a0505] to-[#001510]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
          {/* Purple glow */}
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-900/30 blur-[120px] rounded-full" />
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-900/40 blur-[120px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 min-h-[calc(92vh-3.5rem)] flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-8">
            <Dices className="w-4 h-4 text-red-400" />
            <span className="font-mono text-[11px] tracking-[0.4em] text-red-400 uppercase">Χαλκίδα · Tabletop RPG</span>
          </div>

          <h1 data-testid="hyena-title" style={{ fontFamily: "'Cinzel', 'Outfit', serif" }} className="text-white text-5xl sm:text-7xl lg:text-[9rem] leading-[0.9] uppercase font-black tracking-tight">
            {content.page_title.split(" ").map((w, i) => (
              <span key={i} className={i === 1 ? "text-red-500" : ""}>
                {w}{" "}
              </span>
            ))}
          </h1>

          <p data-testid="hyena-subtitle" style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-6 text-2xl md:text-3xl text-red-200 italic max-w-3xl">
            {content.subtitle}
          </p>

          <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
            {content.supporting_text}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a href="#cta" data-testid="hero-cta-primary" className="group inline-flex items-center gap-3 bg-red-600 text-white font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-red-500 transition-colors border border-red-500/50 shadow-[0_0_40px_-10px_rgba(239,68,68,0.6)]">
              {content.cta_primary_label || "Δήλωσε συμμετοχή"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            {content.social_url && (
              <a href={content.social_url} target="_blank" rel="noreferrer noopener" data-testid="hero-social-secondary" className="inline-flex items-center gap-3 border border-zinc-700 text-white font-bold uppercase tracking-widest px-8 py-4 text-sm hover:border-red-400 hover:text-red-400 transition-colors">
                <ExternalLink className="w-4 h-4" />
                Linktree
              </a>
            )}
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="relative py-24 md:py-32 border-t border-red-950/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-red-500" />
            <span className="font-mono text-[11px] tracking-[0.4em] text-red-400 uppercase">The Story</span>
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-zinc-200 text-lg md:text-xl leading-[1.7] space-y-6">
            {content.description.split("\n\n").map((p, i) => (
              <p key={i} data-testid={`hyena-desc-${i}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="relative py-20 md:py-24 border-t border-red-950/40 bg-gradient-to-b from-black to-[#0a0505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {content.feature_cards.map((c, i) => (
              <div key={i} data-testid={`hyena-feature-${i}`} className="relative border border-red-950/50 bg-black/60 p-6 md:p-7 hover:border-red-500/70 transition-colors group overflow-hidden">
                <div className="absolute top-0 left-0 font-mono text-[10px] tracking-[0.3em] text-red-500/60 px-3 py-1 border-r border-b border-red-950/50">0{i + 1}</div>
                <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-red-600/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <h3 style={{ fontFamily: "'Cinzel', serif" }} className="mt-6 font-bold text-red-400 text-xl uppercase leading-tight">{c.title}</h3>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="relative py-20 md:py-24 border-t border-red-950/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-red-500" />
            <span className="font-mono text-[11px] tracking-[0.4em] text-red-400 uppercase">The Experience</span>
          </div>
          <ul data-testid="hyena-experience-list" className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {content.experience_items.map((item, i) => (
              <li key={i} data-testid={`hyena-exp-${i}`} className="flex items-start gap-3 text-zinc-200">
                <span className="text-red-500 font-mono text-sm mt-0.5">✦</span>
                <span className="text-sm md:text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="relative py-24 md:py-32 border-t border-red-950/40 bg-gradient-to-b from-black to-[#0a0505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-10">
              <span className="h-px w-10 bg-red-500" />
              <span className="font-mono text-[11px] tracking-[0.4em] text-red-400 uppercase">Gallery · {gallery.length} Photos</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {gallery.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setLightbox(m)}
                  data-testid={`hyena-gallery-${m.id}`}
                  className="group relative overflow-hidden border border-red-950/40 hover:border-red-500/70 transition-colors"
                  style={{ aspectRatio: m.width && m.height ? `${m.width}/${m.height}` : "4/3" }}
                >
                  <img
                    src={fileUrl(m.id, "medium")}
                    alt={m.alt_text || m.title || "Hyena Dungeon"}
                    loading="lazy"
                    className="w-full h-full object-contain bg-black group-hover:scale-105 transition-transform duration-700"
                  />
                  {m.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-xs text-red-200">{m.caption}</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QR & LINKTREE */}
      <section className="relative py-24 md:py-32 border-t border-red-950/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-red-500" />
            <span className="font-mono text-[11px] tracking-[0.4em] text-red-400 uppercase">Follow / Contact</span>
            <span className="h-px w-10 bg-red-500" />
          </div>
          <h3 style={{ fontFamily: "'Cinzel', serif" }} className="text-3xl md:text-4xl uppercase text-white font-black">Ακολούθησε το <span className="text-red-500">Hyena Dungeon</span></h3>

          <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-8">
            {qr && (
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-red-500 to-purple-800 blur opacity-40" />
                <img data-testid="hyena-qr" src={fileUrl(qr.id, "medium")} alt={qr.alt_text || "Hyena Dungeon QR"} className="relative w-48 h-48 md:w-56 md:h-56 object-contain bg-black p-2 border border-red-500/60" />
              </div>
            )}
            <div className="max-w-xs text-left">
              <p className="text-zinc-400 text-sm mb-4">Σκάναρε το QR ή πάτα το κουμπί για όλα τα links, socials και events του Hyena Dungeon.</p>
              {content.social_url && (
                <a href={content.social_url} target="_blank" rel="noreferrer noopener" data-testid="hyena-social-cta" className="inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-red-500 transition-colors">
                  <ExternalLink className="w-4 h-4" /> Άνοιξε το Hyena Dungeon Linktree
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* REGISTRATION CTA */}
      <section id="cta" className="relative py-24 md:py-32 border-t border-red-950/40 bg-gradient-to-b from-[#0a0505] to-black overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(220,38,38,0.15),transparent_60%)]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 style={{ fontFamily: "'Cinzel', serif" }} className="text-4xl md:text-6xl uppercase text-white font-black leading-tight">{content.cta_title}</h2>
          <p style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-6 text-xl md:text-2xl text-red-200 italic max-w-2xl mx-auto">{content.cta_text}</p>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 items-stretch justify-center max-w-3xl mx-auto">
            {content.participation_url && (
              <a href={content.participation_url} target={content.participation_url.startsWith("http") ? "_blank" : undefined} rel="noreferrer noopener" data-testid="cta-participation" className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-red-500 transition-colors border border-red-500/50">
                <MessageSquarePlus className="w-4 h-4" /> {content.cta_primary_label || "Δήλωσε συμμετοχή"}
              </a>
            )}
            {content.social_url && (
              <a href={content.social_url} target="_blank" rel="noreferrer noopener" data-testid="cta-social" className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-700 text-white font-bold uppercase tracking-widest px-6 py-4 text-sm hover:border-red-400 hover:text-red-400 transition-colors">
                <ExternalLink className="w-4 h-4" /> {content.cta_social_label || "Ακολούθησέ μας"}
              </a>
            )}
            {content.contact_url && (
              <a href={content.contact_url} target={content.contact_url.startsWith("http") ? "_blank" : undefined} rel="noreferrer noopener" data-testid="cta-contact" className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-700 text-white font-bold uppercase tracking-widest px-6 py-4 text-sm hover:border-red-400 hover:text-red-400 transition-colors">
                <Mail className="w-4 h-4" /> {content.cta_contact_label || "Επικοινώνησε μαζί μας"}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-red-950/40 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Dices className="w-4 h-4 text-red-500" />
            <span style={{ fontFamily: "'Cinzel', serif" }} className="font-bold uppercase text-sm tracking-widest">Hyena <span className="text-red-500">Dungeon</span></span>
          </div>
          <div className="text-xs font-mono text-zinc-600 uppercase tracking-widest">Part of TED Coffee & Fun Spot</div>
          <a href="https://www.instagram.com/hyenadungeon/" target="_blank" rel="noreferrer noopener" className="text-zinc-500 hover:text-red-400 flex items-center gap-2 text-xs">
            <Instagram className="w-4 h-4" /> @hyenadungeon
          </a>
        </div>
      </footer>

      {/* LIGHTBOX */}
      {lightbox && (
        <div data-testid="hyena-lightbox" onClick={() => setLightbox(null)} className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-6">
          <button onClick={() => setLightbox(null)} data-testid="hyena-lightbox-close" className="absolute top-6 right-6 text-white hover:text-red-400 p-2"><X className="w-6 h-6" /></button>
          <img src={fileUrl(lightbox.id, "full")} alt={lightbox.alt_text || ""} className="max-w-full max-h-[85vh] object-contain" />
          {lightbox.caption && <div className="absolute bottom-8 inset-x-0 text-center text-red-200 text-sm px-6">{lightbox.caption}</div>}
        </div>
      )}
    </div>
  );
}
